import { Box, Button, Flex, Input, Menu, Stack, Text, RadioGroup } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { CaretDown, Eye, EyeSlash } from '@phosphor-icons/react';

import { fetchAPI } from '@/api/fetch';
import { apiAdminCreateUserByAdmin, apiAdminGetAgencies, LoginResponse } from '@/api/services';
import { AccountRole, AgencyPlan, getAgencyPlanLabel, getAgencyPlanOptions } from '@/types/enums';

type CreateMode = 'new_agency' | 'existing_agency';

type FormValues = {
  email: string;
  password: string;
  name: string;
  agency_name?: string;
  plan?: { value: AgencyPlan };
  agency_id?: { value: string };
  role?: { value: AccountRole };
};

export default function CreateUser() {
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [user, setUser] = useState<Account | null>(null);
  const [mode, setMode] = useState<CreateMode>('new_agency');
  const [agencies, setAgencies] = useState<Agency[]>([]);

  const {
    control,
    handleSubmit,
    register,
    reset,
    formState: { errors, isSubmitting }
  } = useForm<FormValues>({
    mode: 'onBlur'
  });

  useEffect(() => {
    const fetchAgencies = async () => {
      try {
        const response = await fetchAPI<Agency[]>({
          request: () => apiAdminGetAgencies()
        });
        if (response.success) {
          setAgencies(response.data);
        }
      } catch (error) {
        console.error('Error fetching agencies:', error);
      }
    };

    fetchAgencies();
  }, []);

  const validateEmail = (email: string) => /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(email);

  const handleFormSubmit = async (formData: FormValues) => {
    setSubmitError(null);

    try {
      const basePayload = {
        email: formData.email,
        password: formData.password,
        name: formData.name
      };

      const payload =
        mode === 'new_agency'
          ? {
              ...basePayload,
              agency_name: formData.agency_name!,
              plan: formData.plan!.value
            }
          : {
              ...basePayload,
              agency_id: formData.agency_id!.value,
              role: formData.role!.value
            };

      const response = await fetchAPI<LoginResponse>({
        request: () => apiAdminCreateUserByAdmin({ payload })
      });
      if (response.success) {
        setUser(response.data.account);
        reset();
      } else {
        console.error('Failed to create user:', response.message);
        setSubmitError('Failed to create user. Please try again.');
      }
    } catch (error) {
      console.error('Error creating user:', error);
      setSubmitError('An unexpected error occurred. Please try again.');
    }
  };

  const confirmAction = (message: string, action: () => void) => {
    if (window.confirm(message)) {
      action();
    }
  };

  const clearAction = () => {
    setSubmitError(null);
    setUser(null);
    reset();
  };

  return (
    <Flex w="100%" gap="24px" p="24px">
      <Stack w="100%" gap="16px">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            confirmAction('Are you sure you want to submit?', handleSubmit(handleFormSubmit));
          }}
        >
          <Stack gap="24px">
            <Box>
              <Text mb="8px" fontSize="14px" fontWeight="medium" color="neutrals.700">
                Email
              </Text>
              <Input
                {...register('email', {
                  required: 'Email is required',
                  validate: validateEmail
                })}
                placeholder="Enter email"
                size="lg"
                bg="background.secondary"
                borderColor={errors.email ? 'error.500' : 'inherit'}
                _hover={{ borderColor: errors.email ? 'error.500' : 'inherit' }}
                _focus={{
                  borderColor: errors.email ? 'error.500' : 'primary.900'
                }}
              />
              {errors.email && (
                <Text mt="4px" fontSize="14px" color="error.500">
                  {errors.email.message}
                </Text>
              )}
            </Box>

            <Box>
              <Text mb="8px" fontSize="14px" fontWeight="medium" color="neutrals.700">
                Password
              </Text>
              <Flex alignItems="center" position="relative">
                <Input
                  {...register('password', {
                    required: 'Password is required',
                    minLength: {
                      value: 8,
                      message: 'Password must be at least 8 characters'
                    },
                    maxLength: {
                      value: 30,
                      message: 'Password must be at most 30 characters'
                    },
                    pattern: {
                      value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).+$/,
                      message:
                        'Password must contain at least one uppercase letter, one lowercase letter, and one number'
                    }
                  })}
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter password"
                  size="lg"
                  bg="background.secondary"
                  borderColor={errors.password ? 'error.500' : 'inherit'}
                  _hover={{
                    borderColor: errors.password ? 'error.500' : 'inherit'
                  }}
                  _focus={{
                    borderColor: errors.password ? 'error.500' : 'primary.900'
                  }}
                />
                <Box
                  onClick={() => setShowPassword(!showPassword)}
                  position="absolute"
                  right="10px"
                  bg="transparent"
                  _hover={{ bg: 'transparent', cursor: 'pointer' }}
                  _active={{ bg: 'transparent' }}
                  _focus={{ boxShadow: 'none' }}
                >
                  {showPassword ? <EyeSlash size={24} /> : <Eye color="#000" size={24} />}
                </Box>
              </Flex>
              {errors.password && (
                <Text mt="4px" fontSize="14px" color="error.500">
                  {errors.password.message}
                </Text>
              )}
            </Box>

            <Box>
              <Text mb="8px" fontSize="14px" fontWeight="medium" color="neutrals.700">
                Name
              </Text>
              <Input
                {...register('name', {
                  required: 'Name is required'
                })}
                placeholder="Enter name"
                size="lg"
                bg="background.secondary"
                borderColor={errors.name ? 'error.500' : 'inherit'}
                _hover={{ borderColor: errors.name ? 'error.500' : 'inherit' }}
                _focus={{
                  borderColor: errors.name ? 'error.500' : 'primary.900'
                }}
              />
              {errors.name && (
                <Text mt="4px" fontSize="14px" color="error.500">
                  {errors.name.message}
                </Text>
              )}
            </Box>

            <Box>
              <Text mb="8px" fontSize="14px" fontWeight="medium" color="neutrals.700">
                Creation Mode
              </Text>
              <RadioGroup.Root
                value={mode}
                onValueChange={(e) => {
                  setMode(e.value as CreateMode);
                  reset();
                }}
                colorPalette="blue"
              >
                <Flex gap="16px">
                  <RadioGroup.Item value="new_agency">
                    <RadioGroup.ItemHiddenInput />
                    <RadioGroup.ItemControl />
                    <RadioGroup.ItemText fontSize="16px" color="neutrals.700">
                      Create New Agency
                    </RadioGroup.ItemText>
                  </RadioGroup.Item>
                  <RadioGroup.Item value="existing_agency">
                    <RadioGroup.ItemHiddenInput />
                    <RadioGroup.ItemControl />
                    <RadioGroup.ItemText fontSize="16px" color="neutrals.700">
                      Add to Existing Agency
                    </RadioGroup.ItemText>
                  </RadioGroup.Item>
                </Flex>
              </RadioGroup.Root>
            </Box>

            {mode === 'new_agency' ? (
              <>
                <Box>
                  <Text mb="8px" fontSize="14px" fontWeight="medium" color="neutrals.700">
                    Agency Name
                  </Text>
                  <Input
                    {...register('agency_name', {
                      required: mode === 'new_agency' ? 'Agency name is required' : false
                    })}
                    placeholder="Enter agency name"
                    size="lg"
                    bg="background.secondary"
                    borderColor={errors.agency_name ? 'error.500' : 'inherit'}
                    _hover={{
                      borderColor: errors.agency_name ? 'error.500' : 'inherit'
                    }}
                    _focus={{
                      borderColor: errors.agency_name ? 'error.500' : 'primary.900'
                    }}
                  />
                  {errors.agency_name && (
                    <Text mt="4px" fontSize="14px" color="error.500">
                      {errors.agency_name.message}
                    </Text>
                  )}
                </Box>

                <Box>
                  <Text mb="8px" fontSize="14px" fontWeight="medium" color="neutrals.700">
                    Plan
                  </Text>
                  <Controller
                    name="plan"
                    control={control}
                    rules={{ required: mode === 'new_agency' ? 'Plan is required' : false }}
                    render={({ field }) => (
                      <Menu.Root>
                        <Menu.Trigger asChild>
                          <Button
                            variant="outline"
                            size="lg"
                            width="100%"
                            borderRadius="8px"
                            borderColor={errors.plan ? 'error.500' : 'neutrals.200'}
                            disabled={isSubmitting}
                            bg="background.secondary"
                            _disabled={{
                              bg: 'neutrals.300'
                            }}
                          >
                            <Flex justifyContent="space-between" alignItems="center" width="100%">
                              <Box fontSize="16px" color="neutrals.700">
                                {field.value
                                  ? getAgencyPlanLabel(field.value.value)
                                  : 'Select plan'}
                              </Box>
                              <Box as="span" ml="auto" mr="0">
                                <CaretDown size={16} />
                              </Box>
                            </Flex>
                          </Button>
                        </Menu.Trigger>
                        <Menu.Positioner>
                          <Menu.Content minW="10rem" zIndex={1500}>
                            <Menu.RadioItemGroup
                              value={field.value ? field.value.value : ''}
                              onValueChange={field.onChange}
                            >
                              {getAgencyPlanOptions().map((item) => (
                                <Menu.RadioItem
                                  key={item.value}
                                  value={item.value}
                                  fontSize="16px"
                                  _hover={{ cursor: 'pointer', bg: 'neutrals.100' }}
                                >
                                  {item.label}
                                </Menu.RadioItem>
                              ))}
                            </Menu.RadioItemGroup>
                          </Menu.Content>
                        </Menu.Positioner>
                      </Menu.Root>
                    )}
                  />
                  {errors.plan && (
                    <Text mt="4px" fontSize="14px" color="error.500">
                      {errors.plan.message}
                    </Text>
                  )}
                </Box>
              </>
            ) : (
              <>
                <Box>
                  <Text mb="8px" fontSize="14px" fontWeight="medium" color="neutrals.700">
                    Agency
                  </Text>
                  <Controller
                    name="agency_id"
                    control={control}
                    rules={{ required: mode === 'existing_agency' ? 'Agency is required' : false }}
                    render={({ field }) => (
                      <Menu.Root>
                        <Menu.Trigger asChild>
                          <Button
                            variant="outline"
                            size="lg"
                            width="100%"
                            borderRadius="8px"
                            borderColor={errors.agency_id ? 'error.500' : 'neutrals.200'}
                            disabled={isSubmitting}
                            bg="background.secondary"
                            _disabled={{
                              bg: 'neutrals.300'
                            }}
                          >
                            <Flex justifyContent="space-between" alignItems="center" width="100%">
                              <Box fontSize="16px" color="neutrals.700">
                                {field.value
                                  ? agencies.find((a) => a.agency_id === field.value?.value)
                                      ?.name || 'Select agency'
                                  : 'Select agency'}
                              </Box>
                              <Box as="span" ml="auto" mr="0">
                                <CaretDown size={16} />
                              </Box>
                            </Flex>
                          </Button>
                        </Menu.Trigger>
                        <Menu.Positioner>
                          <Menu.Content minW="10rem" zIndex={1500}>
                            <Menu.RadioItemGroup
                              value={field.value ? field.value.value : ''}
                              onValueChange={field.onChange}
                            >
                              {agencies.map((agency) => (
                                <Menu.RadioItem
                                  key={agency.agency_id}
                                  value={agency.agency_id}
                                  fontSize="16px"
                                  _hover={{ cursor: 'pointer', bg: 'neutrals.100' }}
                                >
                                  {agency.name}
                                </Menu.RadioItem>
                              ))}
                            </Menu.RadioItemGroup>
                          </Menu.Content>
                        </Menu.Positioner>
                      </Menu.Root>
                    )}
                  />
                  {errors.agency_id && (
                    <Text mt="4px" fontSize="14px" color="error.500">
                      {errors.agency_id.message}
                    </Text>
                  )}
                </Box>

                <Box>
                  <Text mb="8px" fontSize="14px" fontWeight="medium" color="neutrals.700">
                    Role
                  </Text>
                  <Controller
                    name="role"
                    control={control}
                    rules={{ required: mode === 'existing_agency' ? 'Role is required' : false }}
                    render={({ field }) => (
                      <Menu.Root>
                        <Menu.Trigger asChild>
                          <Button
                            variant="outline"
                            size="lg"
                            width="100%"
                            borderRadius="8px"
                            borderColor={errors.role ? 'error.500' : 'neutrals.200'}
                            disabled={isSubmitting}
                            bg="background.secondary"
                            _disabled={{
                              bg: 'neutrals.300'
                            }}
                          >
                            <Flex justifyContent="space-between" alignItems="center" width="100%">
                              <Box fontSize="16px" color="neutrals.700">
                                {field.value
                                  ? field.value.value === AccountRole.ADMIN
                                    ? 'Admin'
                                    : 'Member'
                                  : 'Select role'}
                              </Box>
                              <Box as="span" ml="auto" mr="0">
                                <CaretDown size={16} />
                              </Box>
                            </Flex>
                          </Button>
                        </Menu.Trigger>
                        <Menu.Positioner>
                          <Menu.Content minW="10rem" zIndex={1500}>
                            <Menu.RadioItemGroup
                              value={field.value ? field.value.value : ''}
                              onValueChange={field.onChange}
                            >
                              <Menu.RadioItem
                                value={AccountRole.ADMIN}
                                fontSize="16px"
                                _hover={{ cursor: 'pointer', bg: 'neutrals.100' }}
                              >
                                Admin
                              </Menu.RadioItem>
                              <Menu.RadioItem
                                value={AccountRole.MEMBER}
                                fontSize="16px"
                                _hover={{ cursor: 'pointer', bg: 'neutrals.100' }}
                              >
                                Member
                              </Menu.RadioItem>
                            </Menu.RadioItemGroup>
                          </Menu.Content>
                        </Menu.Positioner>
                      </Menu.Root>
                    )}
                  />
                  {errors.role && (
                    <Text mt="4px" fontSize="14px" color="error.500">
                      {errors.role.message}
                    </Text>
                  )}
                </Box>
              </>
            )}

            {submitError && (
              <Text color="error.500" fontSize="14px">
                {submitError}
              </Text>
            )}

            <Flex justify="flex-end" gap="20px" mt="24px">
              <Button
                type="button"
                bg="background.secondary"
                color="primary.900"
                fontSize="16px"
                border="1px solid"
                borderColor="primary.900"
                borderRadius="8px"
                p="24px 32px"
                w="150px"
                onClick={() =>
                  confirmAction('Are you sure you want to clear the form?', clearAction)
                }
              >
                Clear
              </Button>
              <Button
                type="submit"
                loading={isSubmitting}
                bg="primary.900"
                fontSize="16px"
                borderRadius="8px"
                p="24px 32px"
                w="150px"
              >
                Submit
              </Button>
            </Flex>
            {user && (
              <Box
                p="24px"
                border="1px solid"
                borderColor="neutrals.200"
                borderRadius="8px"
                w="fit-content"
              >
                <Stack gap="8px">
                  <Text fontSize="16px" color="primary.900" fontWeight="semibold">
                    Account Information
                  </Text>
                  <Text fontSize="14px" color="neutrals.700">
                    <strong>Account Id:</strong> {user.account_id}
                  </Text>
                  <Text fontSize="14px" color="neutrals.700">
                    <strong>Email:</strong> {user.email}
                  </Text>
                  <Text fontSize="14px" color="neutrals.700">
                    <strong>Name:</strong> {user.name}
                  </Text>
                  <Text fontSize="14px" color="neutrals.700">
                    <strong>Agency Id:</strong> {user.agency.agency_id}
                  </Text>
                  <Text fontSize="14px" color="neutrals.700">
                    <strong>Agency Name:</strong> {user.agency.name}
                  </Text>
                  <Text fontSize="14px" color="neutrals.700">
                    <strong>Plan:</strong> {getAgencyPlanLabel(user.agency.plan)}
                  </Text>
                  <Text fontSize="14px" color="neutrals.700">
                    <strong>Role:</strong> {user.role === AccountRole.ADMIN ? 'Admin' : 'Member'}
                  </Text>
                </Stack>
              </Box>
            )}
          </Stack>
        </form>
      </Stack>
    </Flex>
  );
}
